<template>
  <div v-if="hasAccess">
    <slot></slot>
  </div>
</template>

<script>
import auth from '@/services/auth'

export default {
  name: 'RoleGuard',
  props: {
    // Single role required
    role: {
      type: String,
      default: null
    },
    // Multiple roles (user needs any one)
    roles: {
      type: Array,
      default: () => []
    },
    // Platform-specific requirements
    requirePlatform: {
      type: Boolean,
      default: false
    },
    // Customer-specific requirements  
    requireCustomer: {
      type: Boolean,
      default: false
    },
    // Require admin privileges (platform or organization admin)
    requireAdmin: {
      type: Boolean,
      default: false
    },
    // Require platform-level admin
    requirePlatformAdmin: {
      type: Boolean,
      default: false
    },
    // Require management privileges
    requireManage: {
      type: Boolean,
      default: false
    },
    // Require write access (not read-only)
    requireWrite: {
      type: Boolean,
      default: false
    },
    // Require specific feature access
    requireFeature: {
      type: String,
      default: null
    },
    // Require proprietary feature access (platform only)
    requireProprietary: {
      type: Boolean,
      default: false
    },
    // Subscription tier requirements
    minSubscriptionTier: {
      type: String,
      default: null
    },
    // Invert the logic (show if user DOESN'T have role)
    not: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // Reactive trigger to force re-evaluation when auth state changes
      authVersion: 0
    };
  },
  computed: {
    hasAccess() {
      // Include authVersion to make this computed property reactive to auth changes
      // eslint-disable-next-line no-unused-vars
      const _ = this.authVersion;
      
      // If not authenticated, no access
      if (!auth.isAuthenticated()) {
        return this.not ? true : false;
      }

      let hasRole = false;

      // Platform user requirement
      if (this.requirePlatform) {
        hasRole = auth.isPlatformUser();
      }
      // Customer user requirement
      else if (this.requireCustomer) {
        hasRole = auth.isCustomerUser();
      }
      // Platform admin requirement
      else if (this.requirePlatformAdmin) {
        hasRole = auth.hasPlatformAccess();
      }
      // Admin requirement (any admin)
      else if (this.requireAdmin) {
        hasRole = auth.isAdmin();
      }
      // Management requirement  
      else if (this.requireManage) {
        hasRole = auth.canManage();
      }
      // Write requirement
      else if (this.requireWrite) {
        hasRole = auth.canWrite();
      }
      // Proprietary feature requirement
      else if (this.requireProprietary) {
        hasRole = auth.canAccessProprietaryFeatures();
      }
      // Specific feature requirement
      else if (this.requireFeature) {
        hasRole = auth.canAccessFeature(this.requireFeature);
      }
      // Subscription tier requirement
      else if (this.minSubscriptionTier) {
        hasRole = this.checkSubscriptionTier();
      }
      // Check specific role
      else if (this.role) {
        hasRole = auth.hasRole(this.role);
      }
      // Check multiple roles
      else if (this.roles.length > 0) {
        hasRole = auth.hasAnyRole(this.roles);
      }
      // Default: show if authenticated
      else {
        hasRole = true;
      }

      // Apply inversion if requested
      return this.not ? !hasRole : hasRole;
    }
  },
  mounted() {
    // Listen for auth state changes via storage event (cross-tab)
    this.storageHandler = () => {
      this.authVersion++;
    };
    window.addEventListener('storage', this.storageHandler);
    
    // Listen for custom auth change event (same-tab)
    this.authChangeHandler = () => {
      this.authVersion++;
    };
    window.addEventListener('auth-state-changed', this.authChangeHandler);
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.storageHandler);
    window.removeEventListener('auth-state-changed', this.authChangeHandler);
  },
  methods: {
    checkSubscriptionTier() {
      const tierHierarchy = {
        'TRIAL': 1,
        'STANDARD': 2,
        'PROFESSIONAL': 3,
        'ENTERPRISE': 4,
        'PLATFORM': 5
      };

      const userTier = auth.getSubscriptionTier();
      const requiredTier = this.minSubscriptionTier;

      if (!userTier || !requiredTier) {
        return false;
      }

      return tierHierarchy[userTier] >= tierHierarchy[requiredTier];
    }
  }
}
</script>

<style scoped>
/* No specific styling needed */
</style> 